import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Sheet, Stack, Typography } from '@mui/joy';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { logger } from '../../utils/logger';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export interface ErrorFallbackProps {
  error: Error | null;
  resetErrorBoundary: () => void;
}

/**
 * Default error fallback component
 */
export const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetErrorBoundary 
}) => {
  const { t } = useTranslation();
  
  return (
  <Sheet
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    <Box sx={{ maxWidth: 600, textAlign: 'center' }}>
      <Stack spacing={3} alignItems="center">
        <WarningAmberRoundedIcon 
          sx={{ fontSize: 80, color: 'danger.500' }} 
        />
        
        <Typography level="h1" color="danger">
          {t('common.errorOccurred')}
        </Typography>
        
        <Typography level="body-lg" color="neutral">
          {t('common.errorDescription')}
        </Typography>
        
        {error && (
          <Sheet
            variant="soft"
            color="danger"
            sx={{
              p: 2,
              borderRadius: 'sm',
              width: '100%',
              fontFamily: 'monospace',
              fontSize: 'sm',
              overflow: 'auto',
            }}
          >
            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
              {error.message}
            </Typography>
          </Sheet>
        )}
        
        <Stack direction="row" spacing={2}>
          <Button 
            variant="solid" 
            color="primary"
            onClick={resetErrorBoundary}
          >
            {t('common.tryAgain')}
          </Button>
          
          <Button 
            variant="outlined" 
            color="neutral"
            onClick={() => window.location.href = '/'}
          >
            {t('common.goToHome')}
          </Button>
        </Stack>
        
        <Typography level="body-sm" color="neutral">
          {t('common.errorPersists')}
        </Typography>
      </Stack>
    </Box>
  </Sheet>
);
};

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error details
    logger.error('Error boundary caught an error:', error, {
      componentStack: errorInfo.componentStack,
    });
    
    // TODO: Send to error tracking service
    // trackError(error, {
    //   componentStack: errorInfo.componentStack,
    //   errorBoundary: true,
    // });
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      
      return (
        <FallbackComponent
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
